import { cn } from '@neo/lib/utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@neo/primitives/command'
import { PopoverContent } from '@neo/primitives/popover'
import { X } from 'lucide-react'
import { FlowNodeIconClass } from '../../../config/panel.config'
import { useTypedReactFlow } from '../../../hooks/use-react-flow'
import type { FlowNodeTypes } from '../../../types'
import { toTestId } from '../constants'
import { getMentionableNodes } from '../mentionable-nodes'

const getNodeIcon = (nodeType: string) => {
  const config = FlowNodeIconClass[nodeType as FlowNodeTypes]
  if (!config) return null
  return <div className={cn('rounded p-1', config.class)}>{config.icon}</div>
}

import type { NodeMentionData } from './enhanced-input'

export type { NodeMentionData }

interface NodeMentionPopoverProps {
  onSelect: (node: NodeMentionData) => void
  onClose: () => void
}

export const NodeMentionPopover = ({ onSelect, onClose }: NodeMentionPopoverProps) => {
  const { getNodes } = useTypedReactFlow()

  // getNodes() returns a fresh array each call, so memoizing on it never hits;
  // the list is small — compute directly.
  const nodes = getMentionableNodes(getNodes() || []).sort((a, b) => a.name.localeCompare(b.name))

  const handleNodeSelect = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId)
    if (node) {
      onSelect({ name: node.name, type: node.type })
      onClose()
    }
  }

  return (
    <PopoverContent
      className="p-0 w-[280px]"
      side="top"
      align="start"
      sideOffset={8}
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <Command className="rounded-lg">
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <div className="text-p-600">Mention node</div>
          <button
            type="button"
            onClick={onClose}
            className="text-tertiary hover:text-secondary"
            data-testid="button-close-mention-popover"
          >
            <X size={14} />
          </button>
        </div>
        <CommandInput
          placeholder="Search nodes..."
          className="h-9"
          data-testid="input-search-nodes"
        />
        <CommandList className="max-h-[220px]">
          <CommandEmpty className="py-4 text-s-400 text-tertiary">No nodes found</CommandEmpty>
          <CommandGroup>
            {nodes.map((node) => (
              <CommandItem
                key={node.id}
                value={node.name}
                onSelect={() => handleNodeSelect(node.id)}
                className="flex items-center gap-2 cursor-pointer"
                data-testid={`button-mention-node-${toTestId(node.name)}`}
              >
                {getNodeIcon(node.type)}
                <span className="text-s-400 truncate">{node.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  )
}
